import React, {useMemo, useState} from 'react';
import {t} from 'i18next';
import {Box, Container, Grid} from '@mui/material';
import ExplorerArrSourceSelectedContainer from '../explorer-component/ExplorerArrSourceSelectedContainer';
import FilterPanel, {ActiveFilter, FilterApplyPayload} from './FilterPanel';
import {
  useGetMetaDataFilter,
  useSaveCommunicationMapReportData,
} from '../../../hooks/communicationExplorer/useCommunicationExplorer';
import {ReportNotificationType} from './CommunicationExplorer';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../../shared/constants';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../.../../../../redux/store';
import {useSearchParams} from 'react-router-dom';
import {setComExplorerShowByFilters} from '../../../redux/slice/comExplorerSlice';

export interface ReportPayload {
  sourceName: string; // "SECURITY GROUPS/SEARCHES/FOLDERS"
  source: string[]; // e.g., ["1", "5", "16"]
  filters: FilterItem[];
  showByCount: string | number | null; // e.g., "8"
  showBy: string | number | null; // e.g., "Domain"
  showByVolume: string | number | null; // e.g., "Exchange Volume"
  isDeepDive: boolean; // always pass false
  prevReportId: string; // always "0"
  deepDiveFlowChart: null; // always null
  navigationSourceType: string; // "SECGROUP/SEARCH/FOLDER"
}

export interface FilterItem {
  projectFieldId: string;
  projectFieldName: string;
  dataType: string;
  projectFieldLabel: string;
  type: string;
  operand: string;
  values: string[];
  metaDataID: number;
  text: string;
}

export type SourceItem = {
  id: string;
  label: string;
  data?: any[];
  [key: string]: any;
};

export type SelectedSourceMap = Record<string, SourceItem[] | null>;

interface FilterHeaderProps {
  explorerData: any;
  setReqData: React.Dispatch<
    React.SetStateAction<ReportNotificationType | null>
  >;
  onSourceSelectionsUpdate: (map: SelectedSourceMap) => void;
  setShowBy: React.Dispatch<React.SetStateAction<string | number | null>>;
  onReportRunSuccess: (payload: ReportPayload, dataAsOf: string) => void;
  onClearSourceId: () => void;
}

const convertPayloadToSources = (
  explorerData: any,
  userRole: string
): SourceItem[] => {
  const data = explorerData?.dataAnalysisModelResponse?.model;
  if (!data) return [];
  // Base sources
  const baseSources: SourceItem[] = [
    {
      id: 'securityGroups',
      label: 'Security Groups',
      data: data.securityGroupList ?? [],
    },
    {
      id: 'searches',
      label: 'Searches',
      data: data.searchTreeList ?? [],
    },
    {
      id: 'folders',
      label: 'Folders',
      data: data.folderTreeList ?? [],
    },
  ];

  // Conditional item
  const conditionalItem: SourceItem =
    userRole === 'Review Manager'
      ? {
          id: 'assignments',
          label: 'Assignments',
          data: data.assignmentTreeList ?? [],
        }
      : {
          id: 'projects',
          label: 'Projects',
          data: data.projectList ?? [],
        };

  // Insert conditional item at index 2
  baseSources.splice(2, 0, conditionalItem);
  return baseSources;
};

const generateFilters = (
  activeFilters: ActiveFilter[],
  apiFilters: FilterItem[]
): FilterItem[] => {
  return activeFilters.map((item) => {
    const {id, selectionType, values, type, endDate, startDate} = item;
    const apiFilter = apiFilters.find((api) => api.projectFieldId === id);
    const operand: string =
      selectionType === 'include'
        ? 'IN'
        : selectionType === 'exclude'
          ? 'NOTIN'
          : (selectionType ?? 'IN');
    return {
      projectFieldId: apiFilter?.projectFieldId ?? id,
      projectFieldName: apiFilter?.projectFieldName ?? '',
      dataType: apiFilter?.dataType ?? '',
      projectFieldLabel: apiFilter?.projectFieldLabel ?? '',
      type: apiFilter?.dataType ?? '',
      metaDataID: apiFilter?.metaDataID ?? 0,
      text: apiFilter?.text ?? '',
      operand,
      values:
        type !== 'DATETIME'
          ? (values?.map((v) => v.text) ?? [])
          : [startDate ?? '', endDate ?? ''],
    };
  });
};

const FilterHeader: React.FC<FilterHeaderProps> = ({
  explorerData,
  setReqData,
  onSourceSelectionsUpdate,
  onReportRunSuccess,
  onClearSourceId,
  setShowBy,
}) => {
  const dispatch = useDispatch();
  const [showToast, setShowToast] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [selectedSourcesBySection, setSelectedSourcesBySection] = useState<
    Record<string, any[] | null>
  >({});

  const {
    sourceId: conceptSourceId,
    sourceName,
    navigationSourceType,
  } = useSelector((state: RootState) => state.comExplorer);
  const [, setSearchParams] = useSearchParams();
  const userRole = useSelector(
    (state: RootState) => state?.authenticatedUser?.user?.roleName
  );
  const {mutate: postCommunicationReport, isError} =
    useSaveCommunicationMapReportData();

  const sourceItems = useMemo(() => {
    return convertPayloadToSources(explorerData, userRole ?? '');
  }, [explorerData]);

  const _isSourceNotSelected = useMemo(() => {
    return conceptSourceId
      ? false
      : Object.values(selectedSourcesBySection).every((arr) => {
          if (arr == null) return true;
          if (Array.isArray(arr)) return arr.length === 0;
          return true;
        });
  }, [selectedSourcesBySection]);

  const {lstFilterSqlMetaData = []} =
    explorerData?.dataAnalysisModelResponse?.model || {};

  const {data: metaFilterData = null} = useGetMetaDataFilter({
    sourceTypeCode: 'comminicationmap',
    enabled: !!lstFilterSqlMetaData.length,
  });

  const resetSourceItems = () => {
    setReqData(null);
    setSearchParams({});

    setSelectedSourcesBySection({});
    setResetKey((prev) => prev + 1);
    onClearSourceId();
  };

  const handleSourceSelectionChange = (map: SelectedSourceMap) => {
    setSelectedSourcesBySection(map);
    onSourceSelectionsUpdate(map);
  };

  const handleApplyFilters = ({
    activeFilters,
    topValue,
    categoryValue,
    volumeValue,
    dataAsOf,
  }: FilterApplyPayload) => {
    if (_isSourceNotSelected) {
      setShowToast(true);
    } else {
      const selectedSource = Object.keys(selectedSourcesBySection)[0];
      const sourceDetails =
        selectedSource && selectedSourcesBySection[selectedSource]
          ? selectedSourcesBySection[selectedSource]![0]
          : null;
      const payload: ReportPayload = {
        sourceName: sourceName ?? sourceDetails?.sourceName ?? '',
        source: conceptSourceId
          ? [conceptSourceId]
          : (sourceDetails?.source ?? []),
        navigationSourceType:
          navigationSourceType ?? sourceDetails?.navigationSourceType ?? '',
        filters: generateFilters(activeFilters, metaFilterData),
        showByCount: topValue, // e.g., "8"
        showBy: categoryValue, // e.g., "Domain"
        showByVolume: volumeValue, // e.g., "Exchange Volume"
        isDeepDive: false, // always pass false
        prevReportId: '0', // always "0"
        deepDiveFlowChart: null, // always null
      };
      postCommunicationReport(payload, {
        onSuccess: (data = null) => {
          setReqData(data);
          onReportRunSuccess(payload, dataAsOf);
          dispatch(
            setComExplorerShowByFilters({
              topValue, // e.g., "8"
              categoryValue, // e.g., "Domain"
              volumeValue,
            })
          );
        },
        onError: () => {
          setShowToast(true);
          setShowToast(true);
        },
      });
      setShowBy(categoryValue);
    }
  };

  return (
    <Grid container>
      <Grid size={3} className="explorer-grid">
        <Box sx={{padding: 2, height: '100%'}}>
          <ExplorerArrSourceSelectedContainer
            key={resetKey}
            sources={sourceItems}
            onSelectionsUpdate={handleSourceSelectionChange}
            sourceID={explorerData?.commMap?.sourceID}
            sourceType={explorerData?.commMap?.sourceType}
          />
        </Box>
      </Grid>

      <Grid size={9} className="filters-container">
        <Container>
          <Box sx={{padding: 2}}>
            <FilterPanel
              dataAsOfProp={explorerData?.commMap?.dataAsOf}
              title={t('docExplorer.filterAccordion.title')}
              filters={metaFilterData ?? []}
              onFilterApply={handleApplyFilters}
              displayShowBy
              showValues={{
                topValue: explorerData?.commMap?.showByCount ?? null,
                categoryValue: explorerData?.commMap?.showBy ?? null,
                volumeValue: explorerData?.commMap?.showByCVolume ?? null,
              }}
              disableApply={
                (explorerData?.commMap?.sourceID?.length ?? 0) > 0
                  ? false
                  : _isSourceNotSelected
              }
              resetSourceItems={resetSourceItems}
              selectedFilters={explorerData?.commMap?.filterByMetaData}
            />
          </Box>
        </Container>
      </Grid>
      {showToast && (
        <ArrToastNotification
          message={
            isError
              ? t('filterPanel.toastFail')
              : _isSourceNotSelected
                ? t('filterPanel.selectSourceError')
                : ''
          }
          onClose={() => {
            setShowToast(false);
          }}
          status={[
            isError || _isSourceNotSelected
              ? ToastType.Error
              : ToastType.Success,
          ]}
        />
      )}
    </Grid>
  );
};

export default FilterHeader;
